import React, { useState, useEffect, useCallback } from "react";
import { JsonRpc } from "@proton/js";
import Tooltip from "./Tooltip";
import PortfolioPositionCard from "./PortfolioPositionCard";
import Loader from "./loader/Loader";
import { claimMarketTransaction } from "../utils/crypto";
import {
  computeNetInvested,
  grossCostBasisMicro,
  summarizeMoynPositions,
} from "../utils/moyn";
import { Info } from "lucide-react";
import { TRADING_TOKEN } from "../configs/config";

interface Position {
  composite_key: number;
  account: string;
  outcome_id: number;
  shares: number;
  invested?: number;
  market_id?: number;
  outcome_name?: string;
}

interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
}

interface MarketPositions {
  market_id: number;
  market: any;
  positions: Position[];
  outcomes: Outcome[];
  // Net invested amount for this user in this market (spend - fees), in whole USDTEST
  myInvested?: number;
  claimedShares?: number;
  unclaimedShares?: number;
}

interface PortfolioProps {
  session: any;
}

// A market allows a refund claim through the `claim` action when it has
// been marked refundable by the super-admin and is not yet resolved.
const marketAllowsRefund = (market: any): boolean => {
  return Boolean(market && market.is_refundable && !market.resolved);
};

const Portfolio: React.FC<PortfolioProps> = ({ session }) => {
  const [marketPositions, setMarketPositions] = useState<MarketPositions[]>([]);
  const [loading, setLoading] = useState(true);
  const [pnlData, setPnlData] = useState<{
    totalValue: number;
    invested: number;
    pnl: number;
    unrealized: number;
    realized: number;
  }>({
    totalValue: 0,
    invested: 0,
    pnl: 0,
    unrealized: 0,
    realized: 0,
  });
  const [positionsFilter, setPositionsFilter] = useState<
    "all" | "active" | "illiquid" | "resolved"
  >("all");

  const fetchPortfolio = useCallback(async () => {
    if (!session) return;

    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const [balanceResult, marketsResult] = await Promise.all([
        rpc.get_table_rows({
          code: contractName,
          scope: contractName,
          table: "balances",
          lower_bound: session.auth.actor,
          upper_bound: session.auth.actor,
          limit: 1,
        }),
        rpc.get_table_rows({
          code: contractName,
          scope: contractName,
          table: "markets3",
          limit: 100,
        }),
      ]);

      const outcomesCache = new Map<number, Promise<Outcome[]>>();
      const getOutcomes = (marketId: number): Promise<Outcome[]> => {
        const cached = outcomesCache.get(marketId);
        if (cached) {
          return cached;
        }

        const outcomesPromise = rpc
          .get_table_rows({
            code: contractName,
            scope: marketId.toString(),
            table: "outcomes",
            limit: 100,
          })
          .then((outcomesResult: any) =>
            outcomesResult.rows.map((row: any) => ({
              outcome_id: row.outcome_id,
              name: row.name,
              display_order: row.display_order,
            })),
          );

        outcomesCache.set(marketId, outcomesPromise);
        return outcomesPromise;
      };

      const eligibleMarkets = marketsResult.rows.filter(
        (market: any) => market.version === 2 || market.version === 3,
      );

      const marketPositionResults: Array<MarketPositions | null> =
        await Promise.all(
        eligibleMarkets.map(async (market: any) => {
          try {
            const [outcomes, positionsResult, claimsResult] = await Promise.all([
              getOutcomes(market.id),
              rpc.get_table_rows({
                code: contractName,
                scope: market.id.toString(),
                table: market.version === 3 ? "moynpos" : "poslmsr",
                limit: market.version === 3 ? 1000 : 100,
              }),
              rpc.get_table_rows({
                code: contractName,
                scope: market.id.toString(),
                table: "claims",
                limit: 1000,
              }),
            ]);
            const claimRow = claimsResult.rows.find((row: any) => {
              const source = row?.data ?? row;
              return source.user === session.auth.actor.toString();
            });
            const claimSource = claimRow?.data ?? claimRow ?? {};
            const claimedShares = claimSource.claimed ?? 0;
            const unclaimedShares = claimSource.unclaimed ?? 0;

            if (market.version === 3) {
              const summary = summarizeMoynPositions(
                positionsResult.rows,
                session.auth.actor.toString(),
                market.fee_bps ?? 100,
              );
              const userRows = Object.entries(summary.positionsByOutcome);

              if (userRows.length === 0) {
                return null;
              }

              const positions: Position[] = [];
              for (const [outcomeIdKey, position] of userRows) {
                const outcomeId = Number(outcomeIdKey);
                const sharesYes = position.sharesYes;
                const sharesNo = position.sharesNo;
                const rawRow = positionsResult.rows.find((row: any) => {
                  const source = row?.data ?? row;
                  return (
                    source.user === session.auth.actor.toString() &&
                    Number(source.outcome_id) === outcomeId
                  );
                });
                const rawSource = rawRow?.data ?? rawRow ?? {};
                const outcome = outcomes.find(
                  (o) => o.outcome_id === outcomeId,
                );
                const outcomeName = outcome?.name || `Outcome ${outcomeId}`;

                positions.push({
                  composite_key: outcomeId * 2,
                  account: session.auth.actor.toString(),
                  outcome_id: outcomeId,
                  shares: sharesYes,
                  invested: computeNetInvested(rawSource.yes_cost_basis ?? 0),
                  market_id: market.id,
                  outcome_name: `${outcomeName} YES`,
                });

                positions.push({
                  composite_key: outcomeId * 2 + 1,
                  account: session.auth.actor.toString(),
                  outcome_id: outcomeId,
                  shares: sharesNo,
                  invested: computeNetInvested(rawSource.no_cost_basis ?? 0),
                  market_id: market.id,
                  outcome_name: `${outcomeName} NO`,
                });
              }

              return {
                market_id: market.id,
                market,
                positions,
                outcomes,
                myInvested: summary.netInvested,
                claimedShares,
                unclaimedShares,
              } satisfies MarketPositions;
            }

            const myLmsrPos = positionsResult.rows.find(
              (row: any) => row.user === session.auth.actor.toString(),
            );

            if (!myLmsrPos) {
              return null;
            }

            const sharesYes = myLmsrPos.shares_yes / 1_000_000;
            const sharesNo = myLmsrPos.shares_no / 1_000_000;
            const netInvested = computeNetInvested(
              grossCostBasisMicro(myLmsrPos),
            );
            const positions: Position[] = [];

            const yesOutcome =
              outcomes.find((o) => o.outcome_id === 0) || outcomes[0];
            positions.push({
              composite_key: 0,
              account: session.auth.actor.toString(),
              outcome_id: 0,
              shares: sharesYes,
              invested: computeNetInvested(myLmsrPos.yes_cost_basis ?? 0),
              market_id: market.id,
              outcome_name: yesOutcome?.name || "Yes",
            });

            const noOutcome =
              outcomes.find((o) => o.outcome_id === 1) || outcomes[1];
            positions.push({
              composite_key: 1,
              account: session.auth.actor.toString(),
              outcome_id: 1,
              shares: sharesNo,
              invested: computeNetInvested(myLmsrPos.no_cost_basis ?? 0),
              market_id: market.id,
              outcome_name: noOutcome?.name || "No",
            });

            return {
              market_id: market.id,
              market,
              positions,
              outcomes,
              myInvested: netInvested,
              claimedShares,
              unclaimedShares,
            } satisfies MarketPositions;
          } catch (error) {
            console.error(
              `Error fetching portfolio positions for market ${market.id}:`,
              error,
            );
            return null;
          }
        }),
        );

      const nextMarketPositions = marketPositionResults.filter(
        (result): result is MarketPositions => result !== null,
      );

      setMarketPositions(nextMarketPositions);

      let positionValue = 0;
      let invested = 0;
      for (const mp of nextMarketPositions) {
        for (const pos of mp.positions) {
          positionValue += pos.shares * 0.5;
          invested += pos.shares * 0.5;
        }
      }

      // Parse available balance from "X USDTEST" format
      let availableBalance = 0;
      if (balanceResult.rows.length > 0 && balanceResult.rows[0].funds) {
        const fundsStr = balanceResult.rows[0].funds;
        const parts = fundsStr.split(" ");
        availableBalance = Math.floor(parseFloat(parts[0]) || 0);
      }

      // Total value = available balance + position value
      const totalValue = availableBalance + positionValue;
      const unrealized = positionValue - invested;
      const realized = 0;

      setPnlData({
        totalValue,
        invested,
        pnl: unrealized + realized,
        unrealized,
        realized,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (!session) return;
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 50000);
    return () => clearInterval(interval);
  }, [session, fetchPortfolio]);

  const handleClaim = async (marketId: number) => {
    if (!session) return;

    const mp = marketPositions.find((m) => m.market_id === marketId);
    const isRefund = mp ? marketAllowsRefund(mp.market) : false;

    try {
      await claimMarketTransaction(session, {
        market_id: marketId,
        user: session.auth.actor.toString(),
      });

      alert(
        isRefund
          ? "Refund claimed successfully!"
          : "Winnings claimed successfully!",
      );
      fetchPortfolio();
    } catch (error) {
      console.error("Error claiming:", error);
      alert(`Failed to claim ${isRefund ? "refund" : "winnings"}: ${error}`);
    }
  };

  if (loading) {
    return <Loader label="Loading portfolio..." />;
  }

  return (
    <div className="portfolio">
      <div className="portfolio-header"> 
      <h2>
        My Portfolio
      </h2>
        <Tooltip
          text="View your positions in markets and claim winnings from resolved markets."
          position="right"
        >
          <Info size={14} />
        </Tooltip>
      </div>

      <div className="portfolio-stats">
        <div className="pnl-card">
          <h3>
            Portfolio Value
            <Tooltip
              text="Estimated value of your positions and profit/loss. This is a simplified calculation based on current holdings."
              position="right"
            >
         <Info size={14} />
            </Tooltip>
          </h3>
          <div className="pnl-stats">
            <div className="pnl-item">
              <span className="pnl-label">Total Value:</span>
              <span className="pnl-value">
                {Math.floor(pnlData.totalValue)} {TRADING_TOKEN}
              </span>
            </div>
            <div className="pnl-item">
              <span className="pnl-label">Unrealized P&L:</span>
              <span
                className={`pnl-value ${pnlData.unrealized >= 0 ? "positive" : "negative"}`}
              >
                {pnlData.unrealized >= 0 ? "+" : ""}
                {Math.floor(pnlData.unrealized)} {TRADING_TOKEN}
              </span>
            </div>
            <div className="pnl-item">
              <span className="pnl-label">Total P&L:</span>
              <span
                className={`pnl-value ${pnlData.pnl >= 0 ? "positive" : "negative"}`}
              >
                {pnlData.pnl >= 0 ? "+" : ""}
                {Math.floor(pnlData.pnl)} {TRADING_TOKEN}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="positions-section">
          <h3>
            My Positions
            <Tooltip
              text="Shares you own in each market. When markets resolve, claim your winnings if you predicted correctly."
              position="right"
            >
              <Info size={14} />
            </Tooltip>
          </h3>
          {marketPositions.length > 0 && (
            <div className="positions-status-filter">
              <button
                className={positionsFilter === "all" ? "active" : ""}
                onClick={() => setPositionsFilter("all")}
              >
                All ({marketPositions.length})
              </button>
              <button
                className={positionsFilter === "active" ? "active" : ""}
                onClick={() => setPositionsFilter("active")}
              >
                Active (
                {
                  marketPositions.filter(
                    (mp) =>
                      !mp.market.resolved && !marketAllowsRefund(mp.market),
                  ).length
                }
                )
              </button>
              <button
                className={positionsFilter === "illiquid" ? "active" : ""}
                onClick={() => setPositionsFilter("illiquid")}
              >
                Illiquid (
                {
                  marketPositions.filter((mp) => marketAllowsRefund(mp.market))
                    .length
                }
                )
              </button>
              <button
                className={positionsFilter === "resolved" ? "active" : ""}
                onClick={() => setPositionsFilter("resolved")}
              >
                Resolved (
                {marketPositions.filter((mp) => mp.market.resolved).length})
              </button>
            </div>
          )}
          {marketPositions.length === 0 ? (
            <div className="no-positions">No positions yet</div>
          ) : (
            <div className="positions-list">
              {marketPositions
                .filter((mp) => {
                  if (positionsFilter === "all") return true;
                  const isResolved = Boolean(mp.market.resolved);
                  const allowsRefund = marketAllowsRefund(mp.market);
                  if (positionsFilter === "resolved") return isResolved;
                  if (positionsFilter === "illiquid") return allowsRefund;
                  if (positionsFilter === "active")
                    return !isResolved && !allowsRefund;
                  return true;
                })
                .map((mp) => {
                  return (
                    <PortfolioPositionCard
                      key={mp.market_id}
                      marketPosition={mp}
                      onClaim={handleClaim}
                    />
                  );
                })}
            </div>
          )}
        </div>
    </div>
  );
};

export default Portfolio;
